import { Link, useParams } from "react-router-dom";
import Seo from "@/components/base/Seo";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/base/BackToTop";
import { channels, articles } from "@/mocks/articles";
import { chipSolid } from "@/utils/ui";
import { formatViews } from "@/utils/format";

const site = "https://immaple.cn";

export default function ChannelPage() {
  const { id } = useParams();
  const channel = channels.find((c) => c.id === id);

  if (!channel) {
    return (
      <div className="flex min-h-screen flex-col">
        <Navbar />
        <main className="flex-1 flex flex-col items-center justify-center text-center px-4 py-24">
          <p className="text-6xl font-heading font-bold text-background-300">404</p>
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">没有这个栏目</h1>
          <p className="mt-2 text-sm text-foreground-500">你找的栏目可能已经改名或下架了。</p>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 whitespace-nowrap px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600">
            回到首页吃瓜
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const list = articles
    .filter((a) => a.category === channel.id)
    .sort((a, b) => (b.date > a.date ? 1 : b.date < a.date ? -1 : 0));
  const hot = [...list].sort((a, b) => b.views - a.views)[0];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `在线吃瓜 · ${channel.name}`,
    description: channel.brief,
    url: `${site}/channel/${channel.id}`,
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: site },
        { "@type": "ListItem", position: 2, name: channel.name, item: `${site}/channel/${channel.id}` },
      ],
    },
  };

  return (
    <>
      <Seo
        title={`在线吃瓜 - ${channel.name} ${channel.en}｜栏目文章精选`}
        description={`${channel.brief} 在线吃瓜「${channel.name}」栏目，专注${channel.name}的深度观察与整理，让每个瓜都吃得更透更深。`}
        keywords={`在线吃瓜,${channel.name},在线吃瓜未删细节,在线吃瓜隐藏线索`}
        canonicalPath={`/channel/${channel.id}`}
        jsonLd={jsonLd}
      />
      <div className="flex min-h-screen flex-col">
        <Navbar />
        <main className="flex-1">
          {/* breadcrumb */}
          <div className="bg-background-50 border-b border-background-200">
            <div className="mx-auto max-w-7xl px-4 md:px-6 py-4">
              <nav className="text-xs text-foreground-500" aria-label="面包屑">
                <ol className="flex items-center gap-1.5 flex-wrap">
                  <li><Link to="/" className="hover:text-primary-700">首页</Link></li>
                  <li className="text-foreground-300"><i className="ri-arrow-right-s-line"></i></li>
                  <li className="text-foreground-800 font-medium">{channel.name}</li>
                </ol>
              </nav>
            </div>
          </div>

          {/* header */}
          <section className="bg-background-50">
            <div className="mx-auto max-w-7xl px-4 md:px-6 pb-6 md:pb-8">
              <div className="flex flex-wrap items-center gap-4">
                <span className={`inline-flex items-center justify-center w-12 h-12 rounded-xl font-heading text-xl font-bold ${chipSolid(channel.color)}`}>
                  {channel.name.slice(0, 1)}
                </span>
                <div>
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{channel.name}</h1>
                  <p className="mt-1 text-sm text-foreground-500">
                    {channel.en} · 共 {list.length} 篇精选
                  </p>
                </div>
              </div>
              <p className="mt-4 max-w-3xl text-[15px] leading-7 text-foreground-600">{channel.brief}</p>
            </div>
          </section>

          {hot && (
            <section className="bg-background-100 border-t border-b border-background-200">
              <div className="mx-auto max-w-7xl px-4 md:px-6 py-8 grid grid-cols-1 lg:grid-cols-2 gap-5 items-center">
                <Link to={`/article/${hot.id}`} className="group relative h-60 md:h-72 overflow-hidden rounded-xl border border-background-200">
                  <img src={hot.image} alt={hot.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 to-transparent"></div>
                  <div className="absolute bottom-4 left-5 right-5">
                    <span className="inline-flex items-center gap-1 text-xs text-accent-300">
                      <i className="ri-hotspot-line"></i> 本栏目热度最高
                    </span>
                    <h2 className="mt-1.5 font-heading text-xl font-bold text-background-50 leading-snug line-clamp-2">{hot.title}</h2>
                  </div>
                </Link>
                <div className="p-2">
                  <h3 className="font-heading text-lg font-bold text-foreground-950 leading-snug">{hot.title}</h3>
                  <p className="mt-2.5 text-sm leading-7 text-foreground-600">{hot.subtitle}</p>
                  <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
                    <span className="flex items-center gap-1.5"><i className="ri-user-3-line"></i>{hot.author}</span>
                    <span>{hot.date}</span>
                    <span className="flex items-center gap-1"><i className="ri-fire-line text-primary-500"></i>{formatViews(hot.views)}</span>
                  </div>
                  <Link to={`/article/${hot.id}`} className="mt-5 inline-flex items-center gap-2 whitespace-nowrap px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors">
                    展开阅读全文
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            </section>
          )}

          <section className="bg-background-50">
            <div className="mx-auto max-w-7xl px-4 md:px-6 py-8 md:py-10">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-heading text-lg font-bold text-foreground-950 flex items-center gap-2">
                  <span className="w-1.5 h-5 rounded-full bg-primary-500"></span>
                  栏目文章
                </h2>
                <span className="text-xs text-foreground-500">{list.length} 篇 · 持续更新中</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {list.map((a) => (
                  <Link key={a.id} to={`/article/${a.id}`} className="group flex flex-col overflow-hidden rounded-xl border border-background-200 bg-background-100 hover:border-primary-300 transition-colors">
                    <div className="relative h-44 overflow-hidden">
                      <img src={a.image} alt={a.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      <span className={`absolute left-3 top-3 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${chipSolid(channel.color)}`}>{channel.name}</span>
                    </div>
                    <div className="flex flex-col flex-1 p-4 md:p-5">
                      <h3 className="font-heading text-base font-bold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-700 transition-colors">{a.title}</h3>
                      <p className="mt-2 text-sm text-foreground-600 leading-6 line-clamp-2">{a.subtitle}</p>
                      <div className="mt-auto pt-3 flex items-center justify-between text-xs text-foreground-500">
                        <span className="flex items-center gap-1.5"><i className="ri-user-3-line"></i>{a.author}<span className="hidden sm:inline">· {a.date}</span></span>
                        <span className="flex items-center gap-1 text-primary-600 font-medium whitespace-nowrap">阅读<i className="ri-arrow-right-line"></i></span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </main>
        <Footer />
        <BackToTop />
      </div>
    </>
  );
}